import * as React from 'react';
import {ChangeEvent, ComponentType, forwardRef, KeyboardEvent, useImperativeHandle, useRef} from 'react';
import {FormGroup, InputGroup as BSInputGroup} from 'react-bootstrap';

import {InputGroupStyles} from './InputGroup.styles';
import {Input} from '../Input/Input';

export type InputProperties = {
    as?: string | ComponentType;
    autocorrect?: `on` | `off`;
    borderLeft?: boolean;
    borderRight?: boolean;
    btnText?: string | JSX.Element;
    button?: string | JSX.Element;
    containerClass?: string;
    defaultValue?: string;
    displayCounter?: boolean;
    e2e?: string;
    error?: string;
    inputSize?: `default` | `sm`;
    label?: string;
    maxLength?: number;
    mbUnits?: string;
    name: string;
    onBlur?: (val: ChangeEvent<HTMLInputElement>) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onEnter?: (val: string) => void;
    parentCSS?: string;
    prepend?: boolean;
    type?: 'number' | 'email' | 'text' | 'hidden' | 'password';
    validate?: (value: string) => string;
    value?: string;
} & JSX.IntrinsicElements['input'];

export const InputGroup = forwardRef<HTMLInputElement, InputProperties>(
    (
        {
            autocorrect,
            borderLeft = true,
            borderRight = true,
            btnText,
            button,
            className = ``,
            containerClass,
            defaultValue,
            displayCounter = false,
            e2e,
            error,
            inputSize,
            label,
            maxLength,
            mbUnits = `3`,
            onBlur,
            onChange,
            onEnter,
            parentCSS,
            prepend = false,
            type = 'text',
            value,
            ...other
        },
        ref,
    ) => {
        const inputRef = useRef<HTMLInputElement>();

        /**
         * Handles onChange for Input
         * @param e
         */
        const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
            // If onChange provided, invoke at this time
            if (onChange) {
                onChange(e);
            }
        };

        /**
         * Handles onKeyDown for Input
         * @param e
         */
        const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
            // If onChange provided, invoke at this time
            if (onEnter && e.key === `Enter`) {
                onEnter(inputRef.current.value);
            }
        };

        useImperativeHandle(ref, () => inputRef.current);

        return (
            <InputGroupStyles className={containerClass}>
                <FormGroup className={`!tw-mb-${mbUnits} w-100${error ? ' !tw-mb-8' : ''}`}>
                    <BSInputGroup className={`tw-h-[52px] flex-nowrap ${className ? ' ' + className : ''} mb-0`}>
                        {prepend && (
                            <BSInputGroup.Prepend>
                                <BSInputGroup.Text className={`${other.disabled ? 'gray-100-bg' : 'bg-white'}`}>
                                    {btnText}
                                </BSInputGroup.Text>
                            </BSInputGroup.Prepend>
                        )}
                        <div className={`${parentCSS ? parentCSS + ' ' : ''}tw-w-full${error ? ' tw-mb-0' : ''}`}>
                            <Input
                                autocorrect={autocorrect}
                                className={`${className}`.trim()}
                                clearMargin={true}
                                defaultValue={defaultValue}
                                displayCounter={displayCounter}
                                error={error}
                                e2e={e2e}
                                label={label}
                                maxLength={maxLength}
                                name={other.name}
                                onBlur={(e) => {
                                    if (onBlur) {
                                        onBlur(e);
                                    }
                                }}
                                onChange={handleOnChange}
                                onKeyDown={handleKeyDown}
                                inputSize={inputSize}
                                ref={inputRef}
                                required={other.required}
                                style={borderRight === false && !error ? {borderRight: 0} : {}}
                                value={value}
                                disabled={other.disabled}
                                type={type}
                            />
                        </div>
                        {(!prepend || button) && (
                            <BSInputGroup.Append className="mb-0">
                                {button ? (
                                    button
                                ) : (
                                    <BSInputGroup.Text
                                        className={`${other.disabled ? 'gray-100-bg' : 'bg-white pointer'}`}
                                        style={borderLeft === false ? {borderLeft: 0} : {}}
                                    >
                                        {btnText}
                                    </BSInputGroup.Text>
                                )}
                            </BSInputGroup.Append>
                        )}
                    </BSInputGroup>
                </FormGroup>
            </InputGroupStyles>
        );
    },
);
